import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Select,
  MenuItem,
  InputLabel,
  IconButton,
  FormGroup,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Slide,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import Button from "components/CustomButtons/Button.jsx";
import { apiPath } from "api";
import { generalAuthTokenHeader } from "__helpers/auth-header";
import { userService } from "_services/user.service";
// Active facility = URL ?facilityId= (Org Admin new-tab) -> else own DP facility.
import { getActiveFacilityId } from "__helpers/activeFacility";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="right" ref={ref} {...props} />;
});

/**
 * Facility Admin "Facility Settings" modal — mirrors the MAXMRJ-admin Edit Facility modal
 * (layout, Google Places autocomplete on Address with lat/long sync). Editable fields are
 * name/address/phones/status/lat-long; PCC & identity fields are shown but disabled.
 */
export default function FacilitySettings({ open, onClose, facilityId: facilityIdProp, onSaved }) {
  // Reused by the Org Admin facility list, which passes the row's facilityId explicitly.
  // When no prop is given (DP shell / facility-admin navbar) we fall back to the active facility.
  const facilityId = facilityIdProp != null ? facilityIdProp : getActiveFacilityId();
  const [s, setS] = useState(null);
  const [partnerList, setPartnerList] = useState([]);
  const [loading, setLoading] = useState(false);
  const addressRef = useRef(null);
  const acAttached = useRef(false);

  // Load settings + the API-partner list (for the locked "API Vendor Partner" label) on open.
  useEffect(() => {
    if (!open || !facilityId) return;
    setS(null);
    acAttached.current = false;
    fetch(`${apiPath.facilityAdminFacilities}/${facilityId}/settings`, {
      method: "GET",
      headers: generalAuthTokenHeader(),
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => setS(data || {}))
      .catch(() => setS({}));
    fetch(apiPath.getApiPartnerList, { method: "GET", headers: generalAuthTokenHeader() })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setPartnerList(Array.isArray(data) ? data : []))
      .catch(() => setPartnerList([]));
  }, [open, facilityId]);

  // Attach Google Places autocomplete to the Address input once it is mounted (same as the
  // admin facility form). On selection, fill address + latitude/longitude.
  useEffect(() => {
    if (!open || !s || acAttached.current) return;
    if (!(window.google && window.google.maps && window.google.maps.places)) return;
    const input = addressRef.current;
    if (!input) return;
    const ac = new window.google.maps.places.Autocomplete(input);
    ac.addListener("place_changed", () => {
      const place = ac.getPlace();
      if (place && place.formatted_address) {
        const lat =
          place.geometry && place.geometry.location ? place.geometry.location.lat() : "";
        const lng =
          place.geometry && place.geometry.location ? place.geometry.location.lng() : "";
        setS((prev) => ({
          ...prev,
          address: place.formatted_address,
          latitude: lat,
          longitude: lng,
        }));
      }
    });
    acAttached.current = true;
  }, [open, s]);

  const set = (k, v) => setS((prev) => ({ ...prev, [k]: v }));

  const handleSave = (e) => {
    e.preventDefault();
    setLoading(true);
    fetch(`${apiPath.facilityAdminFacilities}/${facilityId}/settings`, {
      method: "PUT",
      headers: generalAuthTokenHeader(),
      body: JSON.stringify(s),
    })
      .then((r) => {
        setLoading(false);
        if (r.ok) {
          userService.showNotification({
            title: "Facility Settings",
            message: "Settings saved.",
            type: "success",
          });
          if (onSaved) onSaved(); // let the caller (Org Admin list) refresh its rows
          if (onClose) onClose();
        } else {
          userService.showNotification({
            title: "Facility Settings",
            message: "Save failed.",
            type: "danger",
          });
        }
      })
      .catch(() => {
        setLoading(false);
        userService.showNotification({
          title: "Facility Settings",
          message: "Save failed.",
          type: "danger",
        });
      });
  };

  const partnerTitle = (id) => {
    const p = partnerList.find((x) => x.id === id);
    return p ? p.title : id || "";
  };

  return (
    <Dialog
      open={!!open}
      maxWidth={"sm"}
      fullWidth
      onClose={onClose}
      className="add-modal front-modal"
      TransitionComponent={Transition}
    >
      <DialogTitle className="add-modal-title">
        <div className="providerName">Facility Settings</div>
        <IconButton className="closeButton" aria-label="close" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {!facilityId || !s ? (
        <DialogContent>
          <div style={{ padding: 20 }}>Loading…</div>
        </DialogContent>
      ) : (
        <form onSubmit={handleSave} noValidate>
          <DialogContent>
            <Box display="flex" justifyContent="flex-start" mb={2} mt={2}>
              <Box pr={1} width={1}>
                <TextField
                  InputLabelProps={{ className: "required-label" }}
                  name="facilityName"
                  value={s.facilityName || ""}
                  onChange={(e) => set("facilityName", e.target.value)}
                  label="Facility Name"
                />
              </Box>
            </Box>

            <Box display="flex" justifyContent="flex-start" mb={2}>
              <Box width={1 / 2} pr={1}>
                <TextField
                  id="addressMap"
                  name="address"
                  value={s.address || ""}
                  InputLabelProps={{ className: "required-label" }}
                  label="Address Street"
                  onChange={(e) => set("address", e.target.value)}
                  inputRef={addressRef}
                />
              </Box>
              <Box width={1 / 2} pl={1}>
                <TextField
                  InputLabelProps={{ className: "required-label" }}
                  name="primPhone"
                  label="Primary Phone"
                  value={s.primPhone || ""}
                  onChange={(e) => set("primPhone", e.target.value)}
                />
              </Box>
            </Box>

            <Box display="flex" justifyContent="flex-start" mb={2}>
              <Box width={1 / 2} pr={1}>
                <TextField
                  name="secPhone"
                  label="Secondary Phone"
                  value={s.secPhone || ""}
                  onChange={(e) => set("secPhone", e.target.value)}
                />
              </Box>
              <Box width={1 / 2} pl={1}>
                <TextField
                  select
                  fullWidth
                  name="status"
                  label="Status"
                  value={s.status ? "true" : "false"}
                  onChange={(e) => set("status", e.target.value === "true")}
                >
                  <MenuItem value="true">Active</MenuItem>
                  <MenuItem value="false">Inactive</MenuItem>
                </TextField>
              </Box>
            </Box>

            <Box display="flex" justifyContent="flex-start" mb={2}>
              <Box width={1 / 2} pr={1}>
                <TextField
                  name="latitude"
                  value={s.latitude != null ? s.latitude : ""}
                  InputLabelProps={{ className: "required-label" }}
                  label="Latitude"
                  InputProps={{ readOnly: true }}
                />
              </Box>
              <Box width={1 / 2} pl={1}>
                <TextField
                  name="longitude"
                  value={s.longitude != null ? s.longitude : ""}
                  InputLabelProps={{ className: "required-label" }}
                  label="Longitude"
                  InputProps={{ readOnly: true }}
                />
              </Box>
            </Box>
          </DialogContent>

          <DialogActions className="modal-actions" justify="center">
            <Button type="submit" className="btn1" disabled={loading}>
              {loading && <CircularProgress size={24} className="buttonProgress" />}
              Update Facility
            </Button>
            <Button onClick={onClose} className="btn1">
              Cancel
            </Button>
          </DialogActions>
        </form>
      )}
    </Dialog>
  );
}
